import { useEffect, useState, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import LeadsHeader from "../components/LeadsHeader";
import TablaContainer from "./VendedorTableContainer";
import { getUserContext } from "../../auth";
import { apiFetch } from "../../api";

export const EXCLUSIVE_FILTERS = {
  TOTAL: "TOTAL",
  NUEVOS: "NUEVOS",
  ATRASADOS: "ATRASADOS",
};

function PrincipalVendedor() {
  const [leadsData, setLeadsData] = useState(null);
  const [vendedor, setVendedor] = useState(null);
  const [loading, setLoading] = useState(true);

  const [exclusiveFilter, setExclusiveFilter] = useState(EXCLUSIVE_FILTERS.TOTAL);

  const [selectedStates, setSelectedStates] = useState(["ACTIVO"]);

  const [stateCounts, setStateCounts] = useState({ ACTIVO: 0, VENTA: 0, BAJA: 0, FUTURO: 0 });
  const [sortOrder, setSortOrder] = useState('desc')

  // Búsqueda de clientes
  const [searchTerm, setSearchTerm] = useState('');

  const user = getUserContext();
  const vendedorId = user?.vendedor_id ?? user?.id_vendedor ?? null;


  const reloadLeads = useCallback(async () => {
    try {
      if (!vendedorId) return;
      const params = new URLSearchParams();
      params.set("id_vendedor", String(vendedorId));
      if (selectedStates?.length && selectedStates.length < 4) {
        params.set("estados", selectedStates.map(s => s.toUpperCase()).join(","));
      }
      const leadsRes = await apiFetch(`/api/leads?${params.toString()}`);
      if (!leadsRes.ok) throw new Error("Error al obtener leads");
      const leadsJson = await leadsRes.json();
      setLeadsData(leadsJson);
    } catch (error) {
      console.error("❌ Error recargando leads:", error);
    }
  }, [vendedorId, selectedStates]);

  useEffect(() => {
    const fetchData = async () => {
      if (!vendedorId) {
        setLoading(false);
        return; // usuario sin vendedor; manejar UI acorde
      }
      try {
        // 🔹 Llamadas paralelas a los dos endpoints (coherentes con reloadLeads)
        const params = new URLSearchParams();
        params.set("id_vendedor", String(vendedorId));
        if (selectedStates?.length && selectedStates.length < 4) {
          params.set("estados", selectedStates.map(s => s.toUpperCase()).join(","));
        }
        const [leadsRes, vendedorRes] = await Promise.all([
          apiFetch(`/api/leads?${params.toString()}`),
          apiFetch(`/api/vendedores/${vendedorId}`),
        ]);

        if (!leadsRes.ok || !vendedorRes.ok) {
          throw new Error("Error al obtener datos del vendedor o de los leads");
        }

        const leadsJson = await leadsRes.json();
        const vendedorJson = await vendedorRes.json();

        setLeadsData(leadsJson);
        setVendedor(vendedorJson);
      } catch (error) {
        console.error("❌ Error en fetch:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [vendedorId, selectedStates]);

  // Actualizar contadores cuando cambian los estados seleccionados
  useEffect(() => {
    reloadLeads();
  }, [selectedStates, reloadLeads]);

  const handleEstadoUpdated = useCallback(() => {
    // Cada vez que un estado cambie en la tabla, recargamos los leads
    reloadLeads();
  }, [reloadLeads]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!vendedorId) {
    return <Box m={4}>Tu usuario no tiene vendedor asignado.</Box>;
  }
  if (!leadsData || !vendedor) {
    return <Box m={4}>No se pudieron cargar los datos del vendedor o leads.</Box>;
  }

  // Toggle de estados (asegurar al menos uno seleccionado)
  const toggleState = (estado) => {
    setSelectedStates(prev => {
      const exists = prev.includes(estado);
      if (exists) {
        // Evitar dejar vacío
        if (prev.length === 1) return prev;
        return prev.filter(s => s !== estado);
      }
      return [...prev, estado];
    });
  };

  return (
    <Box
      sx={{
        height: "calc(100vh - 60px)",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        overflow: "hidden",
        boxSizing: "border-box",
        px: 5,
        pt: 4,
      }}
    >
      {/* 🔹 Pasamos al header los datos y el nombre */}
      <LeadsHeader
        vendedor={vendedor}
        total={leadsData.total}
        nuevos={leadsData.nuevos}
        atrasados={leadsData.atrasados}
        exclusiveFilter={exclusiveFilter}
        onChangeExclusiveFilter={setExclusiveFilter}
        selectedStates={selectedStates}
        onToggleState={toggleState}
        stateCounts={stateCounts}
        sortOrder={sortOrder}
        onChangeSort={setSortOrder}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      {/* 🔹 Contenido principal */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
        }}
      >
        <TablaContainer 
          vendedorId={vendedorId} 
          exclusiveFilter={exclusiveFilter}
          selectedStates={selectedStates}
          onStateCountsChange={setStateCounts}
          sortOrder={sortOrder}
          onEstadoUpdated={handleEstadoUpdated}
          searchTerm={searchTerm}
        />
      </Box>
    </Box>
  );
}

export default PrincipalVendedor;
